import { useEffect, useRef, useState } from 'react'
import QRCode from 'qrcode'

interface QRCodeWithLogoProps {
  value: string
  size?: number
  logoUrl?: string
  logoSize?: number
  logoPosition?: 'center' | 'top-left' | 'top-right' | 'bottom-left' | 'bottom-right'
  logoBackground?: string
  logoPadding?: number
  className?: string
  title?: string
  errorCorrectionLevel?: 'L' | 'M' | 'Q' | 'H'
  color?: {
    dark: string
    light: string
  }
  margin?: number
}

export function QRCodeWithLogo({
  value,
  size = 200,
  logoUrl,
  logoSize = 40,
  logoPosition = 'center',
  logoBackground = '#FFFFFF',
  logoPadding = 2,
  className = '',
  title = 'QR Code',
  errorCorrectionLevel = 'M',
  color = { dark: '#000000', light: '#FFFFFF' },
  margin = 2
}: QRCodeWithLogoProps) {
  const canvasRef = useRef<HTMLCanvasElement>(null)
  const [isLoading, setIsLoading] = useState(true)
  const [error, setError] = useState<string | null>(null)

  const getLogoPosition = (canvasSize: number, logoSize: number, position: string) => {
    const padding = 10 
    
    switch (position) {
      case 'top-left':
        return { x: padding, y: padding }
      case 'top-right':
        return { x: canvasSize - logoSize - padding, y: padding }
      case 'bottom-left':
        return { x: padding, y: canvasSize - logoSize - padding }
      case 'bottom-right':
        return { x: canvasSize - logoSize - padding, y: canvasSize - logoSize - padding }
      case 'center':
      default:
        return { x: (canvasSize - logoSize) / 2, y: (canvasSize - logoSize) / 2 }
    }
  }

  useEffect(() => {
    const generateQRCode = async () => {
      if (!canvasRef.current) return

      setIsLoading(true)
      setError(null)

      try {
        await QRCode.toCanvas(canvasRef.current, value, {
          width: size,
          margin,
          color,
          errorCorrectionLevel
        })

        if (logoUrl) {
          const canvas = canvasRef.current
          const ctx = canvas.getContext('2d')
          if (!ctx) throw new Error('Could not get canvas context')

          const logo = new Image()
          logo.crossOrigin = 'anonymous'
          
          logo.onload = () => {
            try {
              const { x: logoX, y: logoY } = getLogoPosition(size, logoSize, logoPosition)

              ctx.fillStyle = logoBackground
              ctx.fillRect(
                logoX - logoPadding, 
                logoY - logoPadding, 
                logoSize + (logoPadding * 2), 
                logoSize + (logoPadding * 2)
              )

              ctx.drawImage(logo, logoX, logoY, logoSize, logoSize)
              
              setIsLoading(false)
            } catch (error) {
              console.error('Error drawing logo:', error)
              setError('Failed to add logo')
              setIsLoading(false)
            }
          }

          logo.onerror = () => {
            console.warn('Failed to load logo image')
            setError('Failed to load logo')
            setIsLoading(false)
          }

          logo.src = logoUrl
        } else {
          setIsLoading(false)
        }
      } catch (error) {
        console.error('Error generating QR code:', error)
        setError('Failed to generate QR code')
        setIsLoading(false)
      }
    }

    generateQRCode()
  }, [value, size, logoUrl, logoSize, logoPosition, logoBackground, logoPadding, color, margin, errorCorrectionLevel])

  if (error) {
    return (
      <div className={`flex items-center justify-center ${className}`} style={{ width: size, height: size }}>
        <div className="text-center text-red-500">
          <p className="text-sm">{error}</p>
        </div>
      </div>
    )
  }

  return (
    <div className="relative">
      <canvas
        ref={canvasRef}
        className={className}
        width={size}
        height={size}
        style={{ display: 'block' }}
        title={title}
      />
      {isLoading && (
        <div 
          className="absolute inset-0 flex items-center justify-center bg-white bg-opacity-75"
          style={{ width: size, height: size }}
        >
          <div className="text-center">
            <div className="animate-spin rounded-full h-8 w-8 border-b-2 border-gray-900 mx-auto"></div>
            <p className="text-xs mt-2">Generating...</p>
          </div>
        </div>
      )}
    </div>
  )
} 